import { useTranslation } from 'react-i18next';
import { Paper, Tooltip, IconButton } from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime } from '@utils/helpers';
import theme from '@/theme';

const PackHistoryTable = ({ loading, audits }) => {
    const { t, i18n } = useTranslation();

    const themeWithLocale = createTheme(
        theme,
        i18n.language === 'fr' ? frFR : enUS,
    );

    const formatRebalanceDetails = (details) => {
        return (
            <ul style={{ margin: 0, paddingLeft: 0, listStyleType: 'none' }}>
                {details.map((detail, index) => (
                    <li key={index}>
                        {t('Pack id')}
                        {' '}
                        {detail.id}
                        {' : '}
                        {detail.type === '-'
                            ? (detail.quantity > 1
                                ? t('Removing {{quantity}} credits', { quantity: detail.quantity })
                                : t('Removing {{quantity}} credit', { quantity: detail.quantity }))
                            : (detail.quantity > 1
                                ? t('Adding {{quantity}} credits', { quantity: detail.quantity })
                                : t('Adding {{quantity}} credit', { quantity: detail.quantity }))}
                    </li>
                ))}
            </ul>
        );
    };

    const columns = [
        {
            field: 'date',
            headerName: t('Date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'user',
            headerName: t('User'),
            flex: 1,
            valueGetter: (value, row) => 
                row.user ? `${row.user.first_name} ${row.user.last_name} (${row.user.id})` : '',
        },
        {
            field: 'action',
            headerName: t('Action'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.action === 'rebalance') {
                    return <>
                        {t('audit.rebalance')}
                        <Tooltip title={formatRebalanceDetails(params.row['rebalance-details'])}>
                            <IconButton size="small">
                                <InfoOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    </>;
                }
                return t(`audit.${params.row.action}`);
            },
        },
        {
            field: 'quantity.old',
            headerName: t('Old quantity'),
            flex: 1,
            type: 'number',
            valueGetter: (value, row) => row.action === 'create' ? '-' : row.quantity?.old ?? 0,
        },
        {
            field: 'quantity.new',
            headerName: t('New quantity'),
            flex: 1,
            type: 'number',
            valueGetter: (value, row) => row.quantity?.new ?? 0,
        },
    ];

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper>
                <DataGrid
                    autoHeight
                    loading={loading}
                    rows={loading ? [] : audits.map((audit, index) => ({ ...audit, id: index }))}
                    columns={columns}
                    disableRowSelectionOnClick
                    disableColumnResize={true}
                    disableColumnFilter
                    hideFooter
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'date', sort: 'asc' }],
                        },
                    }}
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'default'
                        }
                    }}
                />
            </Paper>
        </ThemeProvider>
    );
};

export default PackHistoryTable;
